import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../providers/TranslationProvider';
import { SCREENS } from '../utils/constants';
import { ArrowLeft, CheckCircle, RefreshCw, AlertCircle, ShieldCheck } from 'lucide-react';
import LetterGlitch from '../components/LetterGlitch';
import Logo from '../icons/logo.svg';

const VerificationScreen = () => {
  const { navigateToScreen } = useUI();
  const { verifyCode, loading: authLoading } = useAuth(); // Assuming verifyCode exist
  const { t } = useTranslation();

  // States
  const [code, setCode] = useState(['', '', '', '', '', '']); // 6 digit code
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);

  // Theme state
  const [isDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Refs for inputs
  const inputRefs = React.useRef([]);

  // Timer logic
  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => setResendTimer(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  const handleInput = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take last digit
    setCode(newCode);

    // Auto move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLocalError('');

    const fullCode = code.join('');
    if (fullCode.length < 6) {
      setLocalError(t('verify.error.incomplete', 'Please enter the complete valid code.'));
      return;
    }

    setLoading(true);

    try {
      // await verifyCode(fullCode);
      await new Promise(resolve => setTimeout(resolve, 1500)); // Mock
      setSuccess(true);
      setTimeout(() => {
        navigateToScreen(SCREENS.DASHBOARD);
      }, 1000);
    } catch (err) {
      console.error(err);
      setLocalError(err.message || t('verify.error.invalid', 'Invalid verification code.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (resendTimer > 0) return;
    setResendTimer(30);
    // Resend logic here
    console.log("Resending code...");
  };

  // Animation Variants
  const containerVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } }
  };

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textClass = isDarkMode ? 'text-white' : 'text-slate-900';
  const inputBg = isDarkMode ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-blue-100 text-slate-800 placeholder-slate-400';
  const cardBg = isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white/80 border-white';

  return (
    <motion.div
      className={`relative w-full h-screen overflow-hidden flex flex-col items-center justify-center ${bgClass} ${textClass}`}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Dynamic Background */}
      <div className={`absolute inset-0 transition-colors duration-700 ${isDarkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-blue-50 to-white'}`}>
        {isDarkMode && (
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <LetterGlitch
              glitchColors={['#1e40af', '#3b82f6', '#60a5fa']}
              glitchSpeed={50}
              centerVignette={true}
              outerVignette={false}
              smooth={true}
            />
          </div>
        )}
      </div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        onClick={() => navigateToScreen(SCREENS.LOGIN)}
        className="absolute top-6 left-6 z-20 p-2 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Back"
      >
        <ArrowLeft size={24} className={isDarkMode ? 'text-white' : 'text-slate-800'} />
      </motion.button>

      {/* Main Content */}
      <motion.div
        className={`relative z-10 w-full max-w-md p-8 rounded-3xl backdrop-blur-xl border shadow-2xl ${cardBg}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex flex-col items-center mb-8">
          <div className={`w-16 h-16 mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-slate-800 shadow-lg shadow-blue-900/20' : 'bg-white shadow-xl shadow-blue-100'}`}>
            <ShieldCheck className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600">
            {t('verify.title', 'Verification')}
          </h2>
          <p className={`text-sm mt-2 text-center max-w-[80%] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('verify.subtitle', 'Enter the 6-digit code sent to your device.')}
          </p>
        </div>

        {!success ? (
          <form onSubmit={handleVerify} className="space-y-8">

            {/* 6 Digit Input */}
            <div className="flex justify-between gap-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInput(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`w-12 h-14 text-center text-2xl font-bold rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all border ${inputBg}`}
                />
              ))}
            </div>

            {/* Error Message */}
            <AnimatePresence>
              {localError && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm"
                >
                  <AlertCircle size={16} />
                  <span>{localError}</span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-4">
              {/* Submit Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={loading}
                type="submit"
                className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg relative overflow-hidden group ${isDarkMode
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-900/30'
                    : 'bg-blue-600 text-white shadow-blue-200'
                  }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('verify.checking', 'Verifying...')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {t('verify.submit', 'Verify Code')}
                  </span>
                )}
              </motion.button>

              {/* Resend Link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendTimer > 0}
                  className={`text-sm font-medium flex items-center justify-center gap-2 mx-auto ${resendTimer > 0
                      ? (isDarkMode ? 'text-slate-600' : 'text-slate-400')
                      : 'text-blue-500 hover:text-blue-400'
                    }`}
                >
                  <RefreshCw size={14} className={resendTimer > 0 ? 'animate-spin' : ''} />
                  {resendTimer > 0
                    ? t('verify.resendWait', `Resend in ${resendTimer}s`)
                    : t('verify.resend', 'Resend Code')}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="w-24 h-24 mx-auto bg-green-500/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle size={48} className="text-green-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('verify.success', 'Verified Successfully')}</h3>
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {t('verify.redirecting', 'Redirecting to dashboard...')}
            </p>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default VerificationScreen;